import { Validator } from 'fluentvalidation-ts';
import { keyBy, orderBy } from 'lodash';
import { SelectOptions } from '../../../shared/form/inputs/Select';
import { fieldRequiredValidationMessage } from '../../../validators/sharedValidators';
import { beNumeric, haveDecimalPlaces } from '../../../validators/stringValidators';
import {
  BillOfMaterialsOptionalExtraFormModel,
  BillOfMaterialsOptionalExtraFormModelValidator,
  mapBillOfMaterialsOptionalExtraToFormModel,
} from '../../bill-of-materials/billOfMaterialsOptionalExtraFormModel';
import {
  BillOfMaterialsUnitFormModel,
  BillOfMaterialsUnitFormModelValidator,
  mapToBillOfMaterialsUnitFormModel,
} from '../../bill-of-materials/billOfMaterialsUnitFormModel';
import { BillOfMaterialsPartsById } from '../../bill-of-materials/dataForEditBillOfMaterialsForm';
import {
  DataForViewOrEditQuoteBillOfMaterialsForm,
  GuidanceTextTemplate,
  SelectedGuidanceText,
} from './dataForViewOrEditQuoteBillOfMaterialsForm';

export type ManualDiscountTypeCode = 'AdditionalDiscountPercentage' | 'AdditionalDiscountInPounds' | 'TargetMargin';

export const manualDiscountSelectOptions: SelectOptions<ManualDiscountTypeCode> = [
  { value: 'AdditionalDiscountPercentage', text: '% Discount' },
  { value: 'TargetMargin', text: 'Margin' },
  { value: 'AdditionalDiscountInPounds', text: '£ Discount ' },
];

export type ViewOrEditQuoteBillOfMaterialsFormModel = {
  isUsingLegacyPricingModel: boolean;
  customerTypeId: number | null;
  underfloorHeatingQuoteDescription: string | null;
  underfloorHeatingDiscountPercentage?: number;
  underfloorHeatingManualDiscountAmount: string;
  underfloorHeatingManualDiscountTypeCode: ManualDiscountTypeCode;
  heatPumpQuoteDescription: string | null;
  heatPumpDiscountPercentage?: number;
  heatPumpManualDiscountAmount: string;
  heatPumpManualDiscountTypeCode: ManualDiscountTypeCode;
  solarQuoteDescription: string | null;
  solarDiscountPercentage?: number;
  solarManualDiscountAmount: string;
  solarManualDiscountTypeCode: ManualDiscountTypeCode;
  units: Array<BillOfMaterialsUnitFormModel>;
  underfloorHeatingControlOptionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  underfloorHeatingFloorConstructionOptionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  underfloorHeatingCylinderOptionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  solarOptionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  heatPumpOptionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  underfloorHeatingGuidanceText: Array<GuidanceTextFormModel>;
  heatPumpGuidanceText: Array<GuidanceTextFormModel>;
  solarGuidanceText: Array<GuidanceTextFormModel>;
};

export type GuidanceTextFormModel = {
  guidanceTextTemplateId: number;
  isSelected: boolean;
  isCustomised: boolean;
  customTitle: string;
  customBody: string;
};

export const getInitialEditQuoteBillOfMaterialsFormModel = (
  data: DataForViewOrEditQuoteBillOfMaterialsForm,
): ViewOrEditQuoteBillOfMaterialsFormModel => {
  const selectedGuidanceTextByTemplateId = keyBy(data.selectedGuidanceText, gt => gt.guidanceTextTemplateId);

  const { guidanceTextTemplates } = data;

  const underfloorHeatingTemplates = guidanceTextTemplates.filter(template => template.showForUnderfloorHeating);
  const underfloorHeatingGuidanceText = mapGuidanceTextTemplatesToFormModels(
    underfloorHeatingTemplates,
    selectedGuidanceTextByTemplateId,
  );

  const heatPumpTemplates = guidanceTextTemplates.filter(template => template.showForHeatPump);
  const heatPumpGuidanceText = mapGuidanceTextTemplatesToFormModels(
    heatPumpTemplates,
    selectedGuidanceTextByTemplateId,
  );

  const solarTemplates = guidanceTextTemplates.filter(template => template.showForSolar);
  const solarGuidanceText = mapGuidanceTextTemplatesToFormModels(solarTemplates, selectedGuidanceTextByTemplateId);

  return {
    isUsingLegacyPricingModel: data.isUsingLegacyPricingModel,
    customerTypeId: data.customerTypeId,
    underfloorHeatingQuoteDescription: data.underfloorHeatingQuoteDescription,
    underfloorHeatingDiscountPercentage: data.isUsingLegacyPricingModel
      ? data.underfloorHeatingDiscountPercentage
      : undefined,
    underfloorHeatingManualDiscountAmount: data.underfloorHeatingManualDiscountAmount.toString(),
    underfloorHeatingManualDiscountTypeCode: data.underfloorHeatingManualDiscountTypeCode,
    heatPumpQuoteDescription: data.heatPumpQuoteDescription,
    heatPumpDiscountPercentage: data.isUsingLegacyPricingModel ? data.heatPumpDiscountPercentage : undefined,
    heatPumpManualDiscountAmount: data.heatPumpManualDiscountAmount.toString(),
    heatPumpManualDiscountTypeCode: data.heatPumpManualDiscountTypeCode,
    solarQuoteDescription: data.solarQuoteDescription,
    solarDiscountPercentage: data.isUsingLegacyPricingModel ? data.solarDiscountPercentage : undefined,
    solarManualDiscountAmount: data.solarManualDiscountAmount.toString(),
    solarManualDiscountTypeCode: data.solarManualDiscountTypeCode,
    units: data.units.map(mapToBillOfMaterialsUnitFormModel),
    underfloorHeatingControlOptionalExtras: data.underfloorHeatingOptionalExtras.controlOptionalExtras.map(
      mapBillOfMaterialsOptionalExtraToFormModel,
    ),
    underfloorHeatingFloorConstructionOptionalExtras: data.underfloorHeatingOptionalExtras.floorConstructionOptionalExtras.map(
      mapBillOfMaterialsOptionalExtraToFormModel,
    ),
    underfloorHeatingCylinderOptionalExtras: data.underfloorHeatingOptionalExtras.cylinderOptionalExtras.map(
      mapBillOfMaterialsOptionalExtraToFormModel,
    ),
    solarOptionalExtras: data.solarOptionalExtras.map(mapBillOfMaterialsOptionalExtraToFormModel),
    heatPumpOptionalExtras: data.heatPumpOptionalExtras.map(mapBillOfMaterialsOptionalExtraToFormModel),
    underfloorHeatingGuidanceText,
    heatPumpGuidanceText,
    solarGuidanceText,
  };
};

const mapGuidanceTextTemplatesToFormModels = (
  guidanceTextTemplates: Array<GuidanceTextTemplate>,
  selectedGuidanceTextByTemplateId: { [guidanceTextTemplateId in number]: SelectedGuidanceText },
) => {
  return orderBy(guidanceTextTemplates, template => template.displayOrder).map<GuidanceTextFormModel>(template => {
    const selectedGuidanceText = selectedGuidanceTextByTemplateId[template.guidanceTextTemplateId];

    const guidanceTextFormModel: GuidanceTextFormModel = {
      guidanceTextTemplateId: template.guidanceTextTemplateId,
      isSelected: selectedGuidanceText != null,
      isCustomised: selectedGuidanceText != null && selectedGuidanceText.isCustomised,
      customTitle: (selectedGuidanceText && selectedGuidanceText.customTitle) || template.title,
      customBody: (selectedGuidanceText && selectedGuidanceText.customBody) || template.body,
    };

    return guidanceTextFormModel;
  });
};

class GuidanceTextFormModelValidator extends Validator<GuidanceTextFormModel> {
  constructor() {
    super();

    this.ruleFor('customTitle')
      .notEmpty()
      .maxLength(200)
      .when(model => model.isCustomised);

    this.ruleFor('customBody')
      .notEmpty()
      .maxLength(2000)
      .when(model => model.isCustomised);
  }
}

const guidanceTextFormModelValidator = new GuidanceTextFormModelValidator();

export const billOfMaterialsMinDiscountPercentage = -100;
export const billOfMaterialsMaxDiscountPercentage = 30;

export class EditQuoteBillOfMaterialsFormModelValidator extends Validator<ViewOrEditQuoteBillOfMaterialsFormModel> {
  constructor(partsById: BillOfMaterialsPartsById) {
    super();

    this.ruleFor('customerTypeId')
      .notNull()
      .withMessage(fieldRequiredValidationMessage)
      .when(model => !model.isUsingLegacyPricingModel);

    this.ruleFor('customerTypeId')
      .null()
      .withMessage('This field should not be set when using the legacy pricing model')
      .when(model => model.isUsingLegacyPricingModel);

    this.ruleForTransformed('underfloorHeatingManualDiscountAmount', value => (value === undefined ? '' : value))
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(haveDecimalPlaces(2))
      .when(model => model.isUsingLegacyPricingModel);

    this.ruleForTransformed('heatPumpManualDiscountAmount', value => (value === undefined ? '' : value))
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(haveDecimalPlaces(2))
      .when(model => model.isUsingLegacyPricingModel);

    this.ruleForTransformed('solarManualDiscountAmount', value => (value === undefined ? '' : value))
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must(beNumeric)
      .must(haveDecimalPlaces(2))
      .when(model => model.isUsingLegacyPricingModel);

    this.ruleForEach('units').setValidator(() => new BillOfMaterialsUnitFormModelValidator(partsById));

    this.ruleForEach('underfloorHeatingControlOptionalExtras').setValidator(
      () => new BillOfMaterialsOptionalExtraFormModelValidator(partsById),
    );
    this.ruleForEach('underfloorHeatingFloorConstructionOptionalExtras').setValidator(
      () => new BillOfMaterialsOptionalExtraFormModelValidator(partsById),
    );
    this.ruleForEach('underfloorHeatingCylinderOptionalExtras').setValidator(
      () => new BillOfMaterialsOptionalExtraFormModelValidator(partsById),
    );
    this.ruleForEach('solarOptionalExtras').setValidator(
      () => new BillOfMaterialsOptionalExtraFormModelValidator(partsById),
    );
    this.ruleForEach('heatPumpOptionalExtras').setValidator(
      () => new BillOfMaterialsOptionalExtraFormModelValidator(partsById),
    );

    this.ruleForEach('underfloorHeatingGuidanceText').setValidator(() => guidanceTextFormModelValidator);
    this.ruleForEach('heatPumpGuidanceText').setValidator(() => guidanceTextFormModelValidator);
    this.ruleForEach('solarGuidanceText').setValidator(() => guidanceTextFormModelValidator);
  }
}
